import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const socials = [
  {
    label: "Instagram",
    value: "@akankshagrowsbrands",
    href: "https://www.instagram.com/akankshagrowsbrands",
  },
  {
    label: "LinkedIn",
    value: "Akanksha Chandok",
    href: "https://www.linkedin.com/in/akanksha-chandok-2001111aa",
  },
  {
    label: "YouTube",
    value: "@akankshachandok",
    href: "https://www.youtube.com/@akankshachandok",
  },
  {
    label: "Email",
    value: "akanksha@hypmmedia.com",
    href: "mailto:akanksha@hypmmedia.com",
  },
  {
    label: "Phone",
    value: "+91 88510 99588",
    href: "tel:+918851099588",
  },
  {
    label: "Studio",
    value: "hypmmedia.com",
    href: "https://hypmmedia.com",
  },
  {
    label: "Linktree",
    value: "linktr.ee/akankshachandok",
    href: "https://linktr.ee/akankshachandok",
  },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setErrorMsg("Please fill in every field.");
      return;
    }
    if (!validEmail(form.email)) {
      setStatus("error");
      setErrorMsg("That email doesn’t look right.");
      return;
    }
    try {
      setStatus("loading");
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
      setErrorMsg("Something went wrong. Try again in a moment.");
    }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", message: "" });
    setStatus("idle");
    setErrorMsg("");
  };

  return (
    <section
      id="contact"
      className="relative border-t hairline pt-24 md:pt-40 pb-16 md:pb-24"
      data-testid="contact-section"
    >
      <div className="grid grid-cols-12 px-6 md:px-10 lg:px-14 mb-14">
        <div className="col-span-12 md:col-span-3 label">08 — Correspondence</div>
        <div className="col-span-12 md:col-span-9 mt-4 md:mt-0">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: EASE }}
            className="font-serif text-[14vw] md:text-[10vw] leading-[0.9] text-ink"
          >
            Let’s
            <span className="italic"> create.</span>
          </motion.h2>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 md:gap-10 px-6 md:px-10 lg:px-14 border-t hairline pt-16">
        {/* Form column */}
        <div className="col-span-12 md:col-span-7">
          <span className="label">Write a note</span>
          <AnimatePresence mode="wait">
            {status === "success" ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="mt-8"
                data-testid="contact-form-success"
              >
                <p className="font-serif italic text-3xl md:text-5xl text-ink leading-tight">
                  Thank you, {form.name.split(" ")[0] || "friend"}.
                </p>
                <p className="mt-6 font-sans text-ink/80 max-w-lg leading-relaxed">
                  Your note has landed in Akanksha’s inbox. Expect a reply
                  within two working days — usually with an idea already in
                  motion.
                </p>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="contact-form-reset"
                  className="edit-link label mt-10 inline-block"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="mt-8 flex flex-col gap-8"
                data-testid="contact-form"
                noValidate
              >
                <FieldRow>
                  <label htmlFor="c-name" className="label">
                    01 · Name
                  </label>
                  <input
                    id="c-name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    data-testid="contact-input-name"
                    className="form-input"
                    autoComplete="name"
                  />
                </FieldRow>

                <FieldRow>
                  <label htmlFor="c-email" className="label">
                    02 · Email
                  </label>
                  <input
                    id="c-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@studio.com"
                    data-testid="contact-input-email"
                    className="form-input"
                    autoComplete="email"
                  />
                </FieldRow>

                <FieldRow>
                  <label htmlFor="c-message" className="label">
                    03 · Message
                  </label>
                  <textarea
                    id="c-message"
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Tell me about the brand, the season, the ambition…"
                    data-testid="contact-input-message"
                    className="form-input resize-none"
                  />
                </FieldRow>

                {status === "error" && errorMsg && (
                  <p
                    data-testid="contact-form-error"
                    className="text-sm text-crimson font-sans"
                  >
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="contact-form-submit"
                  className="group inline-flex items-center gap-4 w-fit border-t border-b hairline py-5 pr-6 pl-1 disabled:opacity-60 hover:pl-4 transition-[padding] duration-500"
                >
                  <span className="font-serif italic text-3xl md:text-4xl">
                    {status === "loading" ? "Sending…" : "Send note"}
                  </span>
                  <ArrowUpRight
                    className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500"
                    strokeWidth={1}
                  />
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          <style>{`
            .form-input {
              background: transparent;
              border: 0;
              border-bottom: 1px solid rgba(26,24,21,0.18);
              color: #1A1815;
              font-family: "Cormorant Garamond", serif;
              font-size: 1.75rem;
              padding: 0.5rem 0;
              width: 100%;
              outline: none;
              transition: border-color 0.4s cubic-bezier(0.76,0,0.24,1);
            }
            .form-input::placeholder {
              color: #A69C8E;
              font-style: italic;
            }
            .form-input:focus {
              border-bottom-color: #1A1815;
            }
          `}</style>
        </div>

        {/* Contact info column */}
        <div className="col-span-12 md:col-span-5 md:pl-10">
          <span className="label">Or reach directly</span>
          <ul className="mt-8 border-t hairline">
            {socials.map((s) => (
              <li key={s.label} className="border-b hairline">
                <a
                  href={s.href}
                  target={s.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={`contact-social-${s.label.toLowerCase()}`}
                  className="group flex items-center justify-between py-5 hover:pl-2 transition-[padding] duration-500"
                >
                  <div className="flex flex-col">
                    <span className="label">{s.label}</span>
                    <span className="font-serif text-xl md:text-2xl italic mt-1">
                      {s.value}
                    </span>
                  </div>
                  <ArrowUpRight
                    className="w-5 h-5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    strokeWidth={1}
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const FieldRow = ({ children }) => (
  <div className="grid grid-cols-12 items-baseline gap-6 md:gap-10 pb-2">
    <div className="col-span-12 md:col-span-3 pt-3 md:pt-4">
      {children[0]}
    </div>
    <div className="col-span-12 md:col-span-9">{children[1]}</div>
  </div>
);

export default Contact;
